import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api.ts';
import { t, exName as exLabel, exDesc as exDescription } from '../../lib/i18n.ts';
import { Button, Card, EmptyState, Spinner, Tag } from '../../lib/ui.tsx';
import { Ring } from '../../components/charts.tsx';

interface TodayData {
  plan: { id: string; title: string } | null;
  items: {
    id: string;
    exerciseId: string;
    rootMidi: number;
    tempoBpm: number;
    doneToday: boolean;
    isDueToday: boolean;
    timesDoneThisWeek: number;
    frequencyPerWeek: number;
    bestScoreThisWeek: number | null;
    exercise: { id: string; name: string; tags: string; estDurationSec: number; kind: string } | null;
  }[];
  streak: number;
  weekCompletion: number;
  needsRangeTest: boolean;
}

/** C8 · flush the offline practice-result queue whenever Today loads online. */
async function flushOfflineQueue() {
  const raw = localStorage.getItem('solfo.resultQueue');
  if (!raw) return;
  const queue = JSON.parse(raw) as unknown[];
  if (queue.length === 0) return;
  try {
    await api('/api/practice-results/batch', { body: { results: queue } });
    localStorage.removeItem('solfo.resultQueue');
  } catch {
    /* still offline — keep the queue */
  }
}

export default function Today() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['today'],
    queryFn: () => api<TodayData>('/api/today'),
  });

  useEffect(() => {
    void flushOfflineQueue().then(() => qc.invalidateQueries({ queryKey: ['today'] }));
    // C8 · refresh the offline bundle while we have connectivity
    void api('/api/offline-bundle')
      .then((b) => localStorage.setItem('solfo.offlineBundle', JSON.stringify(b)))
      .catch(() => {});
  }, [qc]);

  if (isLoading) return <Spinner />;
  if (!data) return null;

  const due = data.items.filter((i) => i.isDueToday);
  const rest = data.items.filter((i) => !i.isDueToday);

  return (
    <div className="space-y-4">
      {data.needsRangeTest && (
        <Card className="border-brand-200 bg-brand-50">
          <p className="text-sm font-medium text-brand-900">First: let's find your range 🎤</p>
          <p className="mt-1 text-xs text-brand-800">
            {t('rangetest.intro', 'Sing guided glides up and down — we detect your lowest and highest reliable notes so every exercise lands in your voice.')}
          </p>
          <Link to="/app/range-test">
            <Button className="mt-3">{t('rangetest.title', 'Start range test')}</Button>
          </Link>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold">{t('today.title', 'Today')}</h1>
          {data.plan && <p className="text-xs text-slate-500">{data.plan.title}</p>}
        </div>
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-xl font-bold text-brand-700">🔥 {data.streak}</p>
            <p className="text-[11px] text-slate-500">{t('today.streak', 'day streak')}</p>
          </div>
          <Ring value={data.weekCompletion} label="This week" size={72} />
        </div>
      </div>

      {data.items.length === 0 ? (
        <EmptyState
          title={t('today.empty', 'No plan for this week yet.')}
          hint="Your coach will assign one — meanwhile you can warm up any time."
          action={
            <Link to="/app/warmup">
              <Button variant="secondary">{t('today.warmup', 'Start warm-up')}</Button>
            </Link>
          }
        />
      ) : (
        <>
          {due.length > 0 && (
            <div className="space-y-2">
              {due.map((item) => (
                <ExerciseRow key={item.id} item={item} highlight />
              ))}
            </div>
          )}
          {rest.length > 0 && (
            <div className="space-y-2">
              <p className="pt-2 text-xs font-medium tracking-wide text-slate-400 uppercase">Rest of the week</p>
              {rest.map((item) => (
                <ExerciseRow key={item.id} item={item} />
              ))}
            </div>
          )}
          <Link to="/app/warmup" className="block">
            <Card className="border-dashed text-center text-sm text-slate-500 hover:border-brand-300">
              🔥 {t('today.warmup', 'Extra warm-up')} — any time, outside the plan
            </Card>
          </Link>
        </>
      )}
    </div>
  );
}

function ExerciseRow({ item, highlight }: { item: TodayData['items'][number]; highlight?: boolean }) {
  if (!item.exercise) return null;
  const params = new URLSearchParams({
    exerciseId: item.exerciseId,
    planItemId: item.id,
    rootMidi: String(item.rootMidi),
    tempoBpm: String(item.tempoBpm),
    mode: 'plan',
  });
  return (
    <Link to={`/app/player?${params}`} className="block">
      <Card className={highlight ? 'border-brand-300' : item.doneToday ? 'opacity-70' : ''}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">
              {item.doneToday ? '✅ ' : ''}
              {exLabel(item.exercise)}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {Math.round(item.exercise.estDurationSec / 60)} min · {item.timesDoneThisWeek}/{item.frequencyPerWeek} this week
              {item.bestScoreThisWeek !== null && ` · best ${item.bestScoreThisWeek}`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {(item.exercise.tags || '')
              .split(',')
              .filter(Boolean)
              .slice(0, 2)
              .map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            <span className="text-lg text-brand-600">▶</span>
          </div>
        </div>
      </Card>
    </Link>
  );
}
